/**
 * Product Gallery Dots Navigation
 * 
 * Enhances the product media gallery with interactive dots navigation
 * that syncs with the slider position.
 */
class ProductGalleryDots {
  constructor() {
    this.init();
  }

  init() {
    // Initialize after the DOM is fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupDots();
        this.setupDesktopNavigation();
      });
    } else {
      this.setupDots();
      this.setupDesktopNavigation();
    }
  }

  setupDots() {
    // Get all media galleries on the page
    const mediaGalleries = document.querySelectorAll('media-gallery');
    
    if (!mediaGalleries.length) return;
    
    mediaGalleries.forEach(gallery => {
      const dots = gallery.querySelectorAll('.product-gallery-dot');
      const slides = gallery.querySelectorAll('.product__media-item');
      const sliderComponent = gallery.querySelector('slider-component');
      
      if (!dots.length || !slides.length || !sliderComponent) return;
      
      // Set up click handler for dots
      dots.forEach(dot => {
        dot.addEventListener('click', (event) => {
          const dotIndex = parseInt(event.currentTarget.getAttribute('data-index'), 10);
          const targetSlide = slides[dotIndex];
          
          if (targetSlide) {
            // Scroll to the selected slide
            targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            
            // Update active dot
            this.updateActiveDot(gallery, dotIndex);
          }
        });
      });
      
      // Monitor slider position to update dots
      this.observeSliderPosition(gallery, slides, dots);
    });
  }
  
  setupDesktopNavigation() {
    // Get all media galleries on the page
    const mediaGalleries = document.querySelectorAll('media-gallery');
    
    if (!mediaGalleries.length) return;
    
    mediaGalleries.forEach(gallery => {
      const slides = gallery.querySelectorAll('.product__media-item');
      const prevButton = gallery.querySelector('.desktop-gallery-navigation .prev-button');
      const nextButton = gallery.querySelector('.desktop-gallery-navigation .next-button');
      
      if (!slides.length || !prevButton || !nextButton) return;
      
      // Set up previous button click handler
      prevButton.addEventListener('click', () => {
        this.navigateSlides(gallery, slides, -1);
      });
      
      // Set up next button click handler
      nextButton.addEventListener('click', () => {
        this.navigateSlides(gallery, slides, 1);
      });
    });
  }
  
  navigateSlides(gallery, slides, direction) {
    if (!slides.length) return;
    
    // Find the currently active slide
    let activeSlideIndex = -1;
    slides.forEach((slide, index) => {
      if (slide.classList.contains('is-active')) {
        activeSlideIndex = index;
      }
    });
    
    // If no active slide found, find one that's most visible
    if (activeSlideIndex === -1) {
      const mediaList = gallery.querySelector('.product__media-list');
      if (!mediaList) return;
      
      let maxVisibility = 0;
      
      slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const mediaListRect = mediaList.getBoundingClientRect();
        
        // Calculate how much of the slide is visible in the viewport
        const visibleWidth = Math.min(rect.right, mediaListRect.right) - Math.max(rect.left, mediaListRect.left);
        const slideVisibility = visibleWidth / rect.width;
        
        if (slideVisibility > maxVisibility) {
          maxVisibility = slideVisibility;
          activeSlideIndex = index;
        }
      });
    }
    
    // Calculate the target slide index
    let targetIndex = activeSlideIndex + direction;
    
    // Handle boundary cases
    if (targetIndex < 0) targetIndex = 0;
    if (targetIndex >= slides.length) targetIndex = slides.length - 1;
    
    // Use the built-in media gallery methods to change the active media
    const targetMediaId = slides[targetIndex].dataset.mediaId;
    if (targetMediaId && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(targetMediaId, false);
    } else {
      // Fallback method if setActiveMedia is not available
      const thumbnail = gallery.querySelector(`.thumbnail-list__item[data-target="${targetMediaId}"] button`);
      if (thumbnail) {
        thumbnail.click();
      } else {
        // If all else fails, just scroll to the slide
        slides[targetIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        slides[targetIndex].classList.add('is-active');
        
        // Remove active class from other slides
        slides.forEach(slide => {
          if (slide !== slides[targetIndex]) {
            slide.classList.remove('is-active');
          }
        });
      }
    }
    
    // Update active dot
    this.updateActiveDot(gallery, targetIndex);
  }
  
  observeSliderPosition(gallery, slides, dots) {
    if (!slides.length || !dots.length) return;
    
    // Create intersection observer to detect which slide is visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
          // Get the index of the current slide
          const activeSlide = entry.target;
          const activeSlideIndex = Array.from(slides).indexOf(activeSlide);
          
          // Update active dot
          this.updateActiveDot(gallery, activeSlideIndex);
        }
      });
    }, {
      root: gallery.querySelector('.product__media-list'),
      threshold: 0.5,
      rootMargin: '0px'
    });
    
    // Observe all slides
    slides.forEach(slide => {
      observer.observe(slide);
    });
  }
  
  updateActiveDot(gallery, activeIndex) {
    const dots = gallery.querySelectorAll('.product-gallery-dot');
    
    dots.forEach((dot, index) => {
      if (index === activeIndex) {
        dot.classList.add('active');
        dot.setAttribute('aria-selected', 'true');
      } else {
        dot.classList.remove('active');
        dot.setAttribute('aria-selected', 'false');
      }
    });
  }
}

// Initialize the component
new ProductGalleryDots();
